/**
 * Whats New — add/edit modal.
 * Rich text description via the shared CKEditor 5 wrapper.
 */

import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import modalStyles from '@/features/application/styles/modals.module.css'
import { DatePicker } from '@/components/ui/DatePicker'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { whatsNewFormSchema, type WhatsNewFormValues } from '../types/schemas'
import { WhatsNewAttachImagePanel } from './WhatsNewAttachImagePanel'
import { VERSION_TYPES } from '../types'
import type { WhatsNewEditModalProps } from '../types'

function normalizeDate(value: string | undefined): string {
  if (!value) return ''
  // Backend may return ISO datetime or YYYY-MM-DD; normalize to date-only.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export function WhatsNewEditModal({
  isOpen,
  note,
  submitting,
  onClose,
  onSubmit,
}: WhatsNewEditModalProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<WhatsNewFormValues>({
    resolver: zodResolver(whatsNewFormSchema),
    mode: 'onChange',
    defaultValues: {
      version_title: '',
      version_description: '',
      version_date: '',
      version_type: 'NEW_FEATURE',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      version_title: note?.version_title ?? '',
      version_description: note?.version_description ?? '',
      version_date: normalizeDate(note?.version_date),
      version_type: note?.version_type ?? 'NEW_FEATURE',
    })
  }, [isOpen, note, reset])

  const internalSubmit = (values: WhatsNewFormValues) => {
    onSubmit(
      {
        version_title: values.version_title.trim(),
        version_description: values.version_description,
        version_date: values.version_date,
        version_type: values.version_type,
      },
      note?.id,
    )
  }

  const title = note?.id ? 'Edit Whats New Details' : 'Add Whats New Details'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-3xl">
      <form
        onSubmit={handleSubmit(internalSubmit)}
        className="min-w-0 space-y-4 p-2 sm:p-4 md:p-6"
      >
        <div>
          <label htmlFor="whats-new-title" className="block text-sm font-medium text-slate-700 mb-1">
            Title
          </label>
          <input
            id="whats-new-title"
            type="text"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register('version_title')}
          />
          {errors.version_title && (
            <span role="alert" className={`block ${modalStyles.formFieldError}`}>
              {errors.version_title.message}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="whats-new-date"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Date
            </label>
            <Controller
              name="version_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  id="whats-new-date"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.version_date?.message}
                  allowFutureDates
                  showHelperText={false}
                  popperFixed
                  popperPlacement="top-start"
                  placeholder="MM/DD/YYYY"
                  dataTestId="whats-new-date"
                />
              )}
            />
          </div>
          <div>
            <label
              htmlFor="whats-new-type"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Type
            </label>
            <select
              id="whats-new-type"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              {...register('version_type')}
            >
              {(Object.keys(VERSION_TYPES) as Array<keyof typeof VERSION_TYPES>).map((key) => (
                <option key={key} value={key}>
                  {VERSION_TYPES[key]}
                </option>
              ))}
            </select>
            {errors.version_type && (
              <span role="alert" className={`block ${modalStyles.formFieldError}`}>
                {errors.version_type.message}
              </span>
            )}
          </div>
        </div>

        <WhatsNewAttachImagePanel />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <Controller
            name="version_description"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CKEditor4
                id="whats-new-description"
                value={value || ''}
                onChange={onChange}
              />
            )}
          />
          {errors.version_description && (
            <span role="alert" className="mt-1 block text-xs text-red-600">
              {errors.version_description.message}
            </span>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isDirty || submitting}
            className={primaryEntityActionButtonClass}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={secondaryEntityActionButtonClass}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
